/**
 * MasterSceneKnowledge.ts
 * 
 * SINGLE SOURCE OF TRUTH for Master Scene formatting rules.
 * All functions now return English instructions exclusively.
 */

// ============================================================
// 1. FORMATTING RULES
// ============================================================

export function getFormattingRules(_lang: string): string {
   return `🎬 TECHNICAL STANDARDIZATION ENGINE (STRICT MODE)

Your function is to exclusively apply structural formatting technical rules.
You do not evaluate narrative quality, do not suggest creative improvements, and do not modify dramatic content.

APPLICABLE TECHNICAL RULES:

SLUGLINES (SCENE HEADINGS)
- Ensure format: INT./EXT. LOCATION - TIME
- Sluglines always in UPPERCASE.
- Standardize use of consistent single hyphen or dash.
- Remove bold, underline, or graphic styles.
- FORBIDDEN: Do not enclose headings in brackets [ ]. Ex: Use "INT. HOUSE - DAY", never "[INT. HOUSE - DAY]".
- Indicate FLASHBACK preferably at the end of the heading (e.g., INT. LOCATION - DAY - FLASHBACK).
- MANDATORY: If using "CONTINUOUS", you must include a base time. Ex: "EXT. HOUSE - DAY - CONTINUOUS". Never use just "- CONTINUOUS".
- PROHIBITED: Technical description of image color, film texture, or visual memory treatments.
- PROHIBITED: Literary prose constructions like "Suddenly".
- FORBIDDEN: Changing location scale (e.g. EXT. HOUSE -> INT. KITCHEN) mid-action. Maintain scene geography.
- STRICTLY PLAIN TEXT: No HTML tags like <center>, <b>, <i> or Markdown blockquotes (>) are allowed anywhere in the script.

CHARACTERS AND DIALOGUE
- Character names always in UPPERCASE. CAUTION: Characters are NOT SFX or Props. Never categorize a character name (e.g., "Zombie Ant") as an SFX/Sound error (ACT-14).
- MANDATORY: Names like "NARRADOR" or "NARRADOR'S VOICE" must be in ALL CAPS.
- DIALOGUE IS DIALOGUE: Never render dialogue (including narrator) as action paragraphs.
- Remove blank line between name and speech (Glue Rule).
- PARENTHETICALS: Never nest parentheses. Never use ((text)). If a word or phrase is meant to be a parenthetical, it must have only one set of parentheses: (text). MANDATORY: Text must be in lowercase (e.g., "whispers"). NO SPACES: Never use spaces before or inside (e.g., " (text)" or "( text )"). Parentheses must be snug against the internal text and start at the beginning of the line. Also applies to brackets [ ].
- Ensure extensions (V.O.) (O.S.) (CONT'D) on the same line as the name.
- If dialogue continues after page break, insert (CONT'D) automatically.
- GHOST CHARACTER (CHR-04): If a character speaks, they MUST be mentioned in the immediately preceding action. Do not leave "bodiless" voices.
- THE BREAK RULE (DLG-03): It is STRICTLY FORBIDDEN to describe physical actions (e.g., "he walks") inside a dialogue block. Dialogue is for SPEECH ONLY. If there is action, CLOSE the dialogue, write the action line, and OPEN a new dialogue (with (CONT'D) if needed). HEALING RULE: If a character makes a non-verbal sound (e.g., growling, laughing), use an onomatopoeia (e.g., "Grrrrrrr", "Hahaha") as the dialogue.
- NO GHOST CUES (THE ECHO ERROR): A character's name must appear exactly ONCE directly above their dialogue. Do not write the character's name as an isolated action line before their dialogue block.
- DIALOGUE CASING: Dialogue text must be written in standard sentence case. NEVER write entire dialogue blocks in ALL CAPS unless the character is literally screaming at the top of their lungs.
- NO TRANSCRIPTION ECHO (DUPLICATION): NEVER repeat the spoken dialogue text as a scene description (action line) below it. What is spoken belongs ONLY under the character's name. Action lines are for visuals, not for transcribing what was just said.

PARENTHETICALS (DLG-04) — ZERO TOLERANCE:
- SINGLE PURPOSE: A parenthetical serves EXCLUSIVELY to indicate TONE OF VOICE or VOCAL INTENTION. Max 3 words. Own isolated line. Zero hyphens. Zero line breaks.
- PROHIBITED (never write inside parentheses):
  → Emotions / adjectives: (sadly), (angrily), (desperately), (nervously), (happily), (begging)
  → Physical actions or gestures: (looking down), (smiling), (turning away), (nodding), (walking away)
  → More than 3 words: any parenthetical with 4+ words is ILLEGAL
  → Hyphenation or line break: NEVER split a parenthetical across lines (e.g. "(whis-\\npering)" is ILLEGAL)
- ALLOWED: (whispers), (shouts), (sarcastic), (ironic), (quietly), (firmly), (screams), (pleading)
- EXAMPLES:
  ✗ BAD: (sadly) I didn't want this.          → ✓ GOOD: (whispers)\\nI didn't want this.
  ✗ BAD: (looking down) I'm sorry.            → ✓ GOOD: (quietly)\\nI'm sorry. [move look to Action line]
  ✗ BAD: (whis-\\npering) Hello.               → ✓ GOOD: (whispering)\\nHello.
  ✗ BAD: (with great pain and sorrow) No!    → ✓ GOOD: (anguished)\\nNo!

SCENE DESCRIPTION (ACTION)
- FORBIDDEN (ACT-21): Explicit camera direction like "Seen from above", "Aerial", or "POV". Focus on action, not the lens.
- MONTAGES AND LISTS: Never use markdown bullets (▪, *, •, etc). If a list is necessary, use a simple hyphen (-). Keep one action per line. STRICTLY PROHIBITED: Do not duplicate information by creating a list and then writing an explanatory paragraph repeating the exact same beats.
- Wrong: "Seen from above: Swimmers..." -> Right: "Swimmers form a line...".
- ACTION CASING (CRITICAL): Action lines (scene descriptions) MUST be written in standard sentence case. NEVER write an entire action sentence or paragraph in ALL CAPS. Only capitalize a Character's Name on their very first appearance, or specific SOUNDS.
- NO FAKE HEADINGS: Do not turn regular action sentences into ALL CAPS sub-headings.


TRANSIITIONS
- Standardize spelling and punctuation: FADE IN:, FADE OUT., DISSOLVE TO:, SMASH CUT TO:.
- Remove misaligned transitions.

NUMBERING
- Remove manually written page numbers in the script body.
- Remove scene numbering in Spec mode.
- Ensure scene numbering in Shooting mode.

FLASHBACKS
- Remove loose text like "End of flashback".
- End flashback only with a new SLUGLINE.

TITLE PAGE
- Ensure title page contains only: Centered Title, Standardized Authorship Credit.
- Remove extra text or authorship duplications.

CLEANING AND NORMALIZATION
- PROFESSIONAL FINISHING (CAPPING THE PROJECT): A script must not end abruptly. After the final dialogue or action, skip TWO BLANK LINES and insert "FADE OUT." or "THE END.". Nothing should appear after the ending.
- Remove extra AI-inserted metadata (introductions, notes, summaries).

FORBIDDEN SCOPE:
- Do not evaluate if action is filmable.
- Do not modify dialogues for style.
- Do not remove metaphors.
- Do not evaluate emotion or psychology.
- Do not suggest narrative improvements.
`;
}

// ============================================================
// 2. GOLD STANDARD
// ============================================================

export function getGoldStandard(_lang: string): string {
   return `[GOLD STANDARD CORRECTIONS - MASTER SCENE]
Use these examples to ensure correction happens in a SINGLE STEP (100% on 1st pass):

1. PSYCHOLOGY -> BEHAVIOR:
   - BAD: "He is desperate, feeling the weight of the world."
   - GOLD: "He snarls and punches the stone wall."

2. ADJECTIVE -> PHYSICAL MEASURE:
   - BAD: "A gigantic table blocks the way."
   - GOLD: "A three-meter oak table blocks the path."

3. EMOTIONAL PARENTHETICAL → PHYSICAL MODE (DLG-04):
   - BAD: "(sadly) - I didn't want this."
   - GOLD: "(whispers)\\nI didn't want this."
   - BAD: "(looking down) - I'm sorry."
   - GOLD: "(quietly)\\nI'm sorry. [move the look to an Action line]"
   - BAD: "(whis-\\npering) - Hello." [hyphenated parenthetical = ILLEGAL]
   - GOLD: "(whispering)\\nHello."
   - BAD: "(with great pain and sorrow) - No!"
   - GOLD: "(anguished)\\nNo!"
   - RULE: Parentheticals = TONE OF VOICE only. Max 3 words. Own isolated line. Zero hyphens. Zero emotions.

4. NOVELIZATION -> PRESENT ACTION:
   - BAD: "Fear took over him as he ran down the hallway."
   - GOLD: "He sprints down the hallway, panting. His feet pound against the wooden floor."

5. PHYSICAL JUDGMENT -> EVIDENCE:
   - BAD: "She moves slowly and uncoordinatedly."
   - GOLD: "She drags her back leg and trips every two steps."

6. PROHIBITED TERMS -> VISUAL ALTERNATIVE:
   - BAD: "His heavy coat protects him." (Abstract/Invisible)
   - GOLD: "The thick wool coat absorbs the impact of the rain."
   - BAD: "She feels exhausted." (Internal state)
   - GOLD: "She collapses to the ground, panting."

7. GHOST CHARACTER (CHR-04):
   - BAD: (Empty action) -> DIVER: "Let's go."
   - GOLD: "A DIVER adjusts his goggles." -> DIVER: "Let's go."

8. CAMERA ANGLE (ACT-21):
   - BAD: "Seen from above: They swim."
   - GOLD: "They swim in perfect formation."

9. THE BREAK RULE (DLG-03):
   - BAD:
     MARCO
     Hello, how are you? He picks up the knife from the table and cleans the blood. How is it going?
   - GOLD:
     MARCO
     Hello, how are you?
     
     Marco picks up the knife from the table and cleans the blood.
     
     MARCO (CONT'D)
     How is it going?

10. GHOST CUES / ECHO ERROR:
- BAD:
JOHN

JOHN
   I am here.
 - GOLD:
   JOHN
   I am here.

11. DIALOGUE CASING & TRANSCRIPTION ECHO:
- BAD:
MARCOS (V.O.)
AQUI NÃO TREINAMOS SÓ O CORPO, MAS A MENTE.
Aqui não treinamos só o corpo, mas a mente.
 - GOLD:
   MARCOS (V.O.)
   Aqui não treinamos só o corpo, mas a mente.

[GOLDEN RULE FOR SUGGESTIONS]
- CHARACTER INTRODUCTION: The first time a character appears, their name MUST be in ALL CAPS (e.g., MARCOS (20)).
- NEVER suggest "The camera shows", "We see", "We observe" or any camera direction.
- Use only the object or character performing the action.
- Wrong: "The camera shows Marcos sitting."
- Right: "Marcos sits."
`;
}

// ============================================================
// 3. AUDIT CHECKLIST
// ============================================================

export function getAuditChecklist(_lang: string): string {
   return `[MASTER SCENES CHECKLIST]
1. TITLE: First line title ONLY in ALL CAPS.
2. SCENE HEADINGS: INT./EXT. [LOCATION] – [TIME]. ALL CAPS. No bold/underline.
3. TIME CONSISTENCY (FRM-15): Heading time (DAY/NIGHT) must match action. If "Sun sets", heading must be DUSK/SUNSET.
4. ACTION: Physical actions only. SHOW, DON'T TELL. No interpretive verbs.
-> OBJECTIVE CAMERA PRINCIPLE: Camera doesn't film "determination". It films fixed eyes.
-> REPLACEMENT RULE: Do NOT just delete unfilmable terms. REPLACE with visual action.
5. CHARACTER: Name in ALL CAPS in intro. No psychology. Name above dialogue.
6. DIALOGUE & PARENTHETICALS (DLG-04): Parentheticals must indicate PHYSICAL mode (screams, whispers). NO emotions (begging, desperately). Kinetic/Movement verbs (e.g., walking away, handing over, exiting) are PHYSICAL ACTIONS and are allowed; do NOT confuse them with psychological states. MANDATORY: Text must be in lowercase. NO SPACES: Do not use spaces inside or before parentheses/brackets (e.g., " (text)" or "( text )"). Symbols must be snug against the text.!
7. THE BREAK RULE (DLG-03): It is STRICTLY FORBIDDEN to describe physical actions (e.g., "he walks away", "picks up the glass") inside a dialogue block. Dialogue is for SPEECH ONLY. Use action lines to break the dialogue.
8. LOGIC & CONTINUITY (LOG-01 / LOG-02): The script must be logically consistent. 
-> OBJECT PERMANENCE (LOG-01): Error if an object appears "out of nowhere" without being established. (e.g., "John pulls a gun" if he entered empty-handed and didn't pick it up).
-> SPATIAL INCONSISTENCY (LOG-02): Error if actions contradict the physical space. (e.g., "She jumps out the window" in a windowless room).
-> IMPOSSIBLE SETTING (LOG-03): Error if INT. is used for open locations like SEA, SKY, FOREST. Sea is always EXT., even underwater.
9. PROFESSIONAL FINISHING (FRM-17): A script must not end abruptly. After the final dialogue/action, skip TWO BLANK LINES and insert "FADE OUT." or "THE END.". NEVER finish with "CUT TO:".
10. HANGING SCENE HEADINGS (FRM-18): It is PROHIBITED to leave a scene heading alone at the end of a page. If the heading is the last line of the page, it must be moved to the next.
11. NO MARKDOWN RESIDUE (FRM-19): The script must be plain text. The use of blockquotes (>), HTML tags (like <center>), triple backticks (\`\`\`), asterisks, underscores, or hashes is PROHIBITED.
`;
}

// ============================================================
// 4. BLACKLIST
// ============================================================

export function getBlacklist(_lang: string): string {
   return `
   [UNFILMABLE TERMS BLACKLIST]
   - Novelization: "The future hangs by a thread", "friendship blossomed", "silence settles".
- Judgments / Abstractions: "deplorable state", "promising", "sickly".
- Psychology / Internal: "determination", "tense silence", "agony", "flash of anger", "Panic sets in", "feels", "thinks", "exhausted".
- Adjectives / Evaluations: "discreet", "almost invisible", "dull shell". (Note: Visual adjectives like "rusty car" or "dim light" are ALLOWED).
- Narrator: "dark omen", "revenge beginning".
`;
}

// ============================================================
// 5. FORMAT-SPECIFIC KNOWLEDGE
// ============================================================

export function getCriticalRules(_format: string = 'Film'): string {
   return `
## CRITICAL RULES (Visual Bible)
1. **LEI OF "VISIBLE/AUDIBLE":** If the camera cannot see it and the microphone cannot capture it, DO NOT WRITE IT.
   - WRONG: "John, a man who suffered much in childhood..." (Unfilmable)
   - RIGHT: "John (40) stares into the void, an old scar across his brow." (Visual)
2. **PROHIBITION OF ABSTRACT TIME:** Never describe routines or passage of time in present action.
   - WRONG: "He visits the aquarium every day."
   - RIGHT: "The doorman nods to him with familiarity. He doesn't even respond, already knows the way."
3. **NO AMATEUR CAMERA DIRECTION:** Do not use "CAMERA ZOOMS IN", "WE SEE", "LOW ANGLE". Describe the action so the framing is obvious.
4. **NO EMOTIONAL ADJECTIVES:** Do not write "he is sad". Describe the *physiology* of the emotion: slumped shoulders, downcast gaze, trembling hands.
`;
}

// ============================================================
// 6. ROLES & EXECUTION
// ============================================================

export function getRole(_lang: string, format: string = 'Film'): string {
   const ROLES: Record<string, string> = {
      'Film': 'You are a Senior Hollywood Screenwriter and relentless Script Doctor.',
      'Animation': 'You are a Senior Animation Screenwriter (e.g., Pixar/Ghibli) specialist in visual storytelling.',
      'Documentary': 'You are a Senior Documentary Screenwriter and Post-Production Editor.',
      'Advertisement': 'You are an Agency Creative Director (Copywriter) specialist in advertising scripts.',
      'default': 'You are a Senior Hollywood Screenwriter and relentless Script Doctor.'
   };
   return ROLES[format] || ROLES['default'];
}

export function getAuditKnowledge(lang: string, format: string = 'Film'): string {
   let base = `${getGoldStandard(lang)} \n${getAuditChecklist(lang)} \n${getBlacklist(lang)} `;
   if (format === 'Documentary') {
      base += `\n## GOLDEN RULES: DOCUMENTARY (COVERAGE)...`;
   }
   return base;
}

export function getExecutionSteps(_lang: string, format: string = 'Film'): string {
   if (format === 'Documentary') {
      return `
## EXECUTION STEPS (DOCUMENTARY)
1. **Identify**: Who is speaking (Character / Interviewee).
2. **Define**: Where they are recording the interview (A-Roll).
3. **Create**: The list of images (B-Roll) that illustrate what they say.
4. **Write**: The script interleaving the speech (V.O.) with the new image.
5. **Montage Formatting**: Use hyphens (-) for visual lists. Maintain ONE action per line. NEVER duplicate a list with an explanatory paragraph repeating the same information.
6. **Review**: Headings to ensure they all have a hyphen " - ".
`;
   }
   return "";
}
